/**
 * Admin Route Component
 *
 * Protects admin routes by requiring authentication and appropriate admin roles.
 * Redirects unauthorized users to the main overview page with an access denied message.
 */

import React, { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Container, Title, Text, Button, Stack, Alert } from '@mantine/core';
import { IconShieldOff, IconHome } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import { APP_ROUTES } from '../../constants/routes';
import { brandColors, semanticColors } from '../../config/theme';

interface AdminRouteProps {
  children: ReactNode;
  /** Required roles for this route. If empty, any admin role is sufficient. */
  requiredRoles?: string[];
}

/**
 * Protects admin routes by checking authentication and role requirements.
 *
 * Access Logic:
 * - If not authenticated, redirects to sign in
 * - If authenticated but no admin roles, shows access denied
 * - If requiredRoles specified, checks if user has any of those roles
 * - If requiredRoles is empty, any admin role grants access
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({
  children,
  requiredRoles = [],
}) => {
  const { isAuthenticated, isLoading, isAdmin, roles } = useAuth();

  // While loading, show nothing (parent will show loading spinner)
  if (isLoading) {
    return null;
  }

  // If not authenticated, redirect to sign in
  if (!isAuthenticated) {
    return <Navigate to="/auth/signin" replace />;
  }

  // If not an admin at all, show access denied
  if (!isAdmin) {
    return <AccessDenied reason="You do not have administrator access." />;
  }

  // If specific roles are required, check for them
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => roles.includes(role));
    if (!hasRequiredRole) {
      return (
        <AccessDenied
          reason={`This page requires one of the following roles: ${requiredRoles.join(', ')}`}
        />
      );
    }
  }

  // User has access
  return <>{children}</>;
};

/**
 * Access denied component shown when user lacks required permissions.
 */
const AccessDenied: React.FC<{ reason: string }> = ({ reason }) => {
  return (
    <Container size="sm" py="xl">
      <Stack align="center" gap="lg">
        <IconShieldOff size={64} color="var(--mantine-color-red-6)" />

        <Title order={2} ta="center">
          Access Denied
        </Title>

        <Text c={semanticColors.text.secondary} ta="center">
          {reason}
        </Text>

        <Alert color={brandColors.warning} title="Need Access?">
          <Text size="sm">
            If you believe you should have access to this area, please contact your
            system administrator to request the appropriate role assignment.
          </Text>
        </Alert>

        <Button
          component="a"
          href={APP_ROUTES.overview}
          leftSection={<IconHome size={16} />}
        >
          Go to Overview
        </Button>
      </Stack>
    </Container>
  );
};
